import { FC, useId } from "react";

import { Box, Stack } from "@mui/material";

import { DashboardCardSx } from "@/dashboard-design-system/Card";
import SelectedSlotIcon from "@/dashboard-design-system/DashboardSlotSelect/assets/selectedSlot.svg";
import UnselectedSlotIcon from "@/dashboard-design-system/DashboardSlotSelect/assets/unselectedSlot.svg";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip";
import { DashboardDivider } from "@/dashboard-design-system/Divider";
import { DashboardIcon } from "@/dashboard-design-system/Icon";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Info } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

interface ChoiceCardProps {
  value: string;
  title: string;
  description?: string;
  tooltip?: string;
  selected: boolean;
  details?: { text: string; icon: IconElement }[];
  onSelect: () => void;
  "data-test"?: string;
}

export const ChoiceCard: FC<ChoiceCardProps> = ({
  value,
  title,
  description,
  tooltip,
  selected,
  details,
  onSelect,
  "data-test": dataTest,
}) => {
  const descriptionId = useId();

  return (
    <DashboardCardSx
      vibe="brand"
      active={selected}
      noHover={selected}
      fullWidth
      data-test={dataTest}
      sx={(theme) => ({
        position: "relative",
        padding: 2,
        "&:has(input:focus-visible)": {
          outline: `2px solid ${theme.palette.border.brand.intense}`,
          outlineOffset: 2,
        },
      })}
    >
      <Box
        component="input"
        type="radio"
        name="campaign-life-cycle"
        value={value}
        aria-label={title}
        aria-describedby={description || details ? descriptionId : undefined}
        checked={selected}
        onChange={onSelect}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          opacity: 0,
          margin: 0,
          cursor: "pointer",
        }}
      />
      <Stack gap={2}>
        <Stack direction="row" gap={1} alignItems="center" paddingY={1}>
          <Stack flex={1} gap={0.25}>
            <Stack direction="row" gap={0.5} alignItems="center">
              <DashboardTypography variant="subtitle1">{title}</DashboardTypography>
              {tooltip && (
                <DashboardTooltip title={tooltip}>
                  <DashboardIcon size="medium" vibe="text-neutral-intense" sx={{ position: "relative" }}>
                    <Info />
                  </DashboardIcon>
                </DashboardTooltip>
              )}
            </Stack>
            {description && (
              <DashboardTypography id={descriptionId} variant="body2" vibe="text-neutral-quiet">
                {description}
              </DashboardTypography>
            )}
          </Stack>
          <DashboardIcon size="medium">{selected ? <SelectedSlotIcon /> : <UnselectedSlotIcon />}</DashboardIcon>
        </Stack>
        {details && (
          <>
            <DashboardDivider />
            <Stack id={descriptionId} gap={2} paddingY={1}>
              {details.map(({ text, icon }) => (
                <Stack key={text} direction="row" gap={1} alignItems="flex-start">
                  <DashboardIcon size="medium" vibe="text-neutral-moderate" sx={{ flexShrink: 0 }}>
                    {icon}
                  </DashboardIcon>
                  <DashboardTypography variant="body2" vibe="text-neutral-moderate">
                    {text}
                  </DashboardTypography>
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </DashboardCardSx>
  );
};
